import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

/**
 * Gap left below the element so it does not sit flush against the window edge.
 * A spacing choice, not a guess about the page's chrome.
 */
const BOTTOM_GAP_PX = 48

export interface FitViewportHeight {
  /**
   * Attach to the measured element. A callback rather than a ref object because
   * styled-components 2.x does not forward `ref` to the DOM node — pass this to
   * `innerRef`, which is the repo's existing pattern for reaching one.
   */
  setNode: (node: HTMLElement | null) => void
  /** Height in px that ends the element at the bottom of the viewport, or undefined before the first measure. */
  height: number | undefined
}

function isDocumentShell(element: Element): boolean {
  return element === document.body || element === document.documentElement
}

/**
 * Watch everything that can move `node` down the page: ancestors, and previous
 * siblings at each level (the chrome above this element). `html`/`body` are
 * `height: 100%` in this app, so observing `documentElement` alone never fires
 * when a banner or alert appears — the viewport size is unchanged, only the
 * element's `top` is.
 *
 * MutationObserver is what picks up a *new* sibling (an error alert mounting).
 * ResizeObserver then watches that sibling so later wraps/resizes also measure.
 * Child-list only, not subtree: ag-grid mutates constantly inside `node`, and
 * that must not retrigger a measure. Stops before `body` so antd portals
 * (tooltips, dropdowns, modals) do not retrigger a measure on every hover.
 */
function observeLayoutContext(
  node: HTMLElement,
  resizeObserver: ResizeObserver,
  mutationObserver: MutationObserver
) {
  resizeObserver.disconnect()
  mutationObserver.disconnect()

  let current: HTMLElement | null = node
  while (current) {
    const parent = current.parentElement
    if (!parent || isDocumentShell(parent)) break

    resizeObserver.observe(parent)
    mutationObserver.observe(parent, { childList: true })

    for (
      let sibling = parent.firstElementChild;
      sibling && sibling !== current;
      sibling = sibling.nextElementSibling
    ) {
      resizeObserver.observe(sibling)
    }

    current = parent
  }
}

/**
 * Sizes an element to end at the bottom of the viewport, whatever is above it.
 *
 * The alternative — `calc(100vh - 400px)`, the pattern used elsewhere in this
 * repo — needs the page's chrome above the element expressed as a constant, and
 * every one of those constants is a guess that rots: this page's was wrong by
 * enough to push the pagination below the fold once a tab bar appeared.
 * Measuring `getBoundingClientRect().top` asks the browser instead, so the fit
 * holds at any window size and survives the toolbar wrapping or a banner
 * appearing above the table.
 *
 * Re-measures on window resize, when anything above the element is added or
 * resizes, and whenever `deps` change. Callers should not have to enumerate
 * banners, alerts, and tabs — those are layout, not state this component owns.
 */
export function useFitViewportHeight(deps: unknown[] = []): FitViewportHeight {
  const nodeRef = useRef<HTMLElement | null>(null)
  const [attachedNode, setAttachedNode] = useState<HTMLElement | null>(null)
  const [height, setHeight] = useState<number | undefined>(undefined)

  const measure = useCallback(() => {
    const element = nodeRef.current
    if (!element) return
    const available =
      window.innerHeight - element.getBoundingClientRect().top - BOTTOM_GAP_PX
    setHeight((previous) => {
      const next = Math.round(available)
      // Sub-pixel jitter from zoom or a scrollbar appearing would otherwise
      // re-render on every measure.
      return previous !== undefined && Math.abs(previous - next) < 2
        ? previous
        : next
    })
  }, [])

  const setNode = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node
      setAttachedNode((current) => (current === node ? current : node))
      if (node) measure()
    },
    [measure]
  )

  // Layout effect so the first paint already has the right height — with a
  // plain effect the table renders at its natural size and visibly resizes.
  useLayoutEffect(() => {
    measure()
  }, [measure, ...deps])

  useEffect(() => {
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  useEffect(() => {
    if (!attachedNode) return
    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(() => measure())
    const mutationObserver = new MutationObserver(() => {
      observeLayoutContext(attachedNode, resizeObserver, mutationObserver)
      measure()
    })

    observeLayoutContext(attachedNode, resizeObserver, mutationObserver)
    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [attachedNode, measure])

  return { setNode, height }
}
